import { createContext, FC, useContext, useMemo } from 'react'
import { useQuery } from 'react-query'
import { logFactory } from '../../util/logFactory'
import { useSearchParameters } from '../useSearchParameters'
import { CustomerProfile, CustomerProfileRequest } from '../../types/customerProfileService'
import { useCustomerProfileService } from '../services/customerProfileService'

interface CustomerProfileContext {
  customerProfile?: CustomerProfile
  isError: boolean
  isLoading: boolean
}

const context = createContext<CustomerProfileContext>({
  customerProfile: undefined,
  isError: false,
  isLoading: true,
})

const hookName = 'useCustomerProfile'
const log = logFactory(hookName)

const { Provider } = context

const CustomerProfileProvider: FC = ({ children }) => {
  const { lead_gid } = useSearchParameters()
  const { getCustomerProfile, isReady } = useCustomerProfileService()

  const customerProfileSearchParams: CustomerProfileRequest = useMemo(() => {
    return { anyRelationId: lead_gid as string }
  }, [lead_gid])

  const fetchCustomerProfile = async (): Promise<CustomerProfile> => {
    log(`👶 Fetching customer profile...`, { customerProfileSearchParams })
    const customerProfile = await getCustomerProfile(customerProfileSearchParams)
    log(`👶 Found customer profile`, customerProfile)

    return customerProfile
  }

  const isQueryEnabled = Boolean(customerProfileSearchParams && isReady && lead_gid)

  const {
    data: customerProfile,
    isError,
    isLoading,
  } = useQuery([hookName, customerProfileSearchParams], fetchCustomerProfile, {
    enabled: isQueryEnabled,
  })

  return (
    <Provider
      value={{
        customerProfile,
        isError,
        isLoading: !isReady || isLoading,
      }}
    >
      {children}
    </Provider>
  )
}

const useCustomerProfile = (): CustomerProfileContext => useContext(context)

export { CustomerProfileProvider, useCustomerProfile }
